import { Keypair, PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js'

/**
 * Minimal wallet adapter interface compatible with Phantom, Solflare,
 * and the Solana wallet-adapter standard.
 */
export interface WalletAdapter {
  publicKey: PublicKey | null
  connected: boolean
  connect(): Promise<void>
  disconnect(): Promise<void>
  signTransaction<T extends Transaction | VersionedTransaction>(tx: T): Promise<T>
  signAllTransactions<T extends Transaction | VersionedTransaction>(txs: T[]): Promise<T[]>
}

/**
 * Wallet adapter backed by a local Keypair for localnet development.
 * Signs transactions directly without browser extension.
 */
class LocalKeypairWallet implements WalletAdapter {
  publicKey: PublicKey | null = null
  connected = false
  private keypair: Keypair

  constructor(keypair: Keypair) {
    this.keypair = keypair
  }

  async connect(): Promise<void> {
    this.publicKey = this.keypair.publicKey
    this.connected = true
  }

  async disconnect(): Promise<void> {
    this.publicKey = null
    this.connected = false
  }

  async signTransaction<T extends Transaction | VersionedTransaction>(tx: T): Promise<T> {
    if (tx instanceof Transaction) {
      tx.partialSign(this.keypair)
    }
    return tx
  }

  async signAllTransactions<T extends Transaction | VersionedTransaction>(txs: T[]): Promise<T[]> {
    for (const tx of txs) {
      if (tx instanceof Transaction) {
        tx.partialSign(this.keypair)
      }
    }
    return txs
  }
}

/** Hardcoded local dev keypair (same as ~/.config/solana/id.json default dev wallet). */
let localKeypair: Keypair | null = null

/**
 * Set the local keypair bytes for localnet development.
 * Call this at boot time with the dev wallet secret key.
 */
export function setLocalKeypair(secretKey: Uint8Array): void {
  localKeypair = Keypair.fromSecretKey(secretKey)
}

let currentWallet: WalletAdapter | null = null

/** Return the currently connected wallet adapter, or null. */
export function getWallet(): WalletAdapter | null {
  return currentWallet
}

/** Return the connected wallet's public key, or null. */
export function getPublicKey(): PublicKey | null {
  return currentWallet?.publicKey ?? null
}

/** True when a wallet is connected and has a public key. */
export function isConnected(): boolean {
  return currentWallet?.connected ?? false
}

export type WalletType = 'phantom' | 'solflare' | 'dev'

export interface AvailableWallet {
  type: WalletType
  name: string
}

/**
 * List wallets available in the current environment.
 */
export function getAvailableWallets(): AvailableWallet[] {
  const wallets: AvailableWallet[] = []

  if (getPhantomProvider()) wallets.push({ type: 'phantom', name: 'Phantom' })
  if (getSolflareProvider()) wallets.push({ type: 'solflare', name: 'Solflare' })
  if (localKeypair) wallets.push({ type: 'dev', name: 'Dev Wallet' })

  return wallets
}

/**
 * Connect a specific wallet by type.
 *
 * @returns The connected wallet's PublicKey.
 * @throws If the provider is unavailable or the user rejects.
 */
export async function connectWallet(type: WalletType): Promise<PublicKey> {
  let adapter: WalletAdapter

  switch (type) {
    case 'phantom': {
      const provider = getPhantomProvider()
      if (!provider) throw new Error('Phantom not found')
      adapter = provider as WalletAdapter
      break
    }
    case 'solflare': {
      const provider = getSolflareProvider()
      if (!provider) throw new Error('Solflare not found')
      adapter = provider as WalletAdapter
      break
    }
    case 'dev': {
      if (!localKeypair) throw new Error('No local keypair set. Call setLocalKeypair() first.')
      adapter = new LocalKeypairWallet(localKeypair)
      break
    }
  }

  await adapter.connect()
  if (!adapter.publicKey) throw new Error('Wallet connected but no public key available')

  currentWallet = adapter
  return adapter.publicKey
}

/** Disconnect the current wallet and clear cached state. */
export async function disconnectWallet(): Promise<void> {
  if (currentWallet) {
    await currentWallet.disconnect()
    currentWallet = null
  }
}

// ---------------------------------------------------------------------------
// Provider detection helpers
// ---------------------------------------------------------------------------

function getPhantomProvider(): unknown | null {
  if (typeof window !== 'undefined' && 'solana' in window) {
    const provider = (window as Record<string, unknown>).solana
    if (provider && typeof provider === 'object' && 'isPhantom' in provider) {
      return provider
    }
  }
  return null
}

function getSolflareProvider(): unknown | null {
  if (typeof window !== 'undefined' && 'solflare' in window) {
    return (window as Record<string, unknown>).solflare
  }
  return null
}
